import type { Theme } from '@goodtime/engine';
import { buildSlotAssets } from './slotAssets';

const ASSETS = buildSlotAssets('dragon', {
  cherry:  'dragon_scale',
  lemon:   'gauntlet',
  orange:  'dragon_shield',
  bell:    'ruby_pile',
  star:    'dragon_wild',
  seven:   'sword_stone',
  gem:     'gold_pile',
  scatter: 'fire_ring_scatter',
  money:   'dragon_egg',
});

/** Dragon's Hoard — Kingdom alt slot. Ember/gold palette, treasure
 *  iconography with a touch of menace (sword in stone, dragon scales).
 *  Symbol-id mapping mirrors {@link FOREST_THEME}; physics differ.
 *
 *  <p><b>Mechanical personality — "Volatile Treasure Hunter":</b>
 *  <ul>
 *    <li>Premium symbols (dragon_wild, sword_stone, gold_pile,
 *        fire_ring_scatter, dragon_egg) are rare — weight 0.5 each
 *        vs Forest's 1.0+. The dragon hoards its loot.
 *    <li>Common symbols (scale, gauntlet, shield) are weighted higher
 *        so the reels chew through fillers fast, making the premium
 *        hits feel earned.
 *    <li>Wheel skews bimodal — twice as many zero segments as Forest
 *        (the dragon kills your run) AND a x50 jackpot (the dragon
 *        opens the vault). No theme has a bigger top end.
 *  </ul>
 *  Intended feel: boom-or-bust. Long droughts punctuated by hoard
 *  cracks. The math favors the dragon — until it doesn't. */
export const DRAGON_THEME: Theme = {
  id: 'dragon',
  name: "Dragon's Hoard",
  palette: {
    accent: 0xff8c42,         // molten orange
    secondary: 0xffc857,      // hoard gold
    warm: 0xffe49a,           // ember warm
    frameFill: 0x1a0e0a,      // obsidian
    bgGradientTop: 0x0e0808,
    bgGradientBottom: 0x3a1a0a,
    dimOverlay: 0x05080d,
  },
  slot: {
    symbols: [
      // Sparse premium distribution — commons (scale, gauntlet,
      // shield) take the bulk of reel weight, while premiums (wild,
      // sword, gold, scatter, egg) drop to 0.5 each. The dragon
      // doesn't part with its hoard easily.
      { id: 'cherry',  label: 'Scale',      glyph: '🟥', tint: 0xc73e3e, weight: 6,   textureAlias: 'dragon:cherry',
        winVideoAlias: 'dragon:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Gauntlet',   glyph: '🪡', tint: 0xc8ccd4, weight: 6,   textureAlias: 'dragon:lemon',
        winVideoAlias: 'dragon:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Shield',     glyph: '🛡', tint: 0xc73e3e, weight: 5,   textureAlias: 'dragon:orange',
        winVideoAlias: 'dragon:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Rubies',     glyph: '💎', tint: 0xe8344e, weight: 4,   textureAlias: 'dragon:bell',
        winVideoAlias: 'dragon:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Dragon',     glyph: '🐉', tint: 0xff8c42, weight: 1,   textureAlias: 'dragon:star', // wild — rare
        winVideoAlias: 'dragon:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Sword',      glyph: '⚔️', tint: 0x3e5baf, weight: 0.5, textureAlias: 'dragon:seven',
        winVideoAlias: 'dragon:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Gold',       glyph: '🪙', tint: 0xffc857, weight: 0.5, textureAlias: 'dragon:gem',
        winVideoAlias: 'dragon:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Fire Ring',  glyph: '🔥', tint: 0xff8c42, weight: 0.5, textureAlias: 'dragon:scatter',
        winVideoAlias: 'dragon:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Egg',        glyph: '🥚', tint: 0xc73e3e, weight: 0.5,
        textureAlias: 'dragon:money',
        winVideoAlias: 'dragon:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    // Bimodal: three zero segments (Forest has two) AND a x50
    // jackpot — bigger than any other theme. Bust often; when you
    // hit, the vault opens.
    segments: [
      { id: 'x0-a',     kind: 'multiplier', label: '✕',   tint: 0x1a0e0a, multiplier: 0,  weight: 4 },
      { id: 'x1-a',     kind: 'multiplier', label: '×1',  tint: 0xff8c42, multiplier: 1,  weight: 4 },
      { id: 'x0-b',     kind: 'multiplier', label: '✕',   tint: 0x1a0e0a, multiplier: 0,  weight: 4 },
      { id: 'x2-a',     kind: 'multiplier', label: '×2',  tint: 0xffc857, multiplier: 2,  weight: 3 },
      { id: 'x1-b',     kind: 'multiplier', label: '×1',  tint: 0xff8c42, multiplier: 1,  weight: 3 },
      { id: 'x3-a',     kind: 'multiplier', label: '×3',  tint: 0xe8344e, multiplier: 3,  weight: 2 },
      { id: 'x0-c',     kind: 'multiplier', label: '✕',   tint: 0x1a0e0a, multiplier: 0,  weight: 3 },
      { id: 'x5-a',     kind: 'multiplier', label: '×5',  tint: 0xffe49a, multiplier: 5,  weight: 2 },
      { id: 'x2-b',     kind: 'multiplier', label: '×2',  tint: 0xffc857, multiplier: 2,  weight: 2 },
      { id: 'x10-a',    kind: 'multiplier', label: '×10', tint: 0xffc857, multiplier: 10, weight: 1 },
      { id: 'x25-a',    kind: 'multiplier', label: '×25', tint: 0xe8344e, multiplier: 25, weight: 1 },
      { id: 'jackpot',  kind: 'multiplier', label: '×50', tint: 0xff8c42, multiplier: 50, weight: 1 },
    ],
  },
  assets: {
    slot: ASSETS.slot,
    slotWin: ASSETS.slotWin,
    scene: {
      background:       { alias: 'dragon:bg',         path: 'environment/dragon.png' },
      columnBackground: { alias: 'dragon:column_bg',  path: 'reel/dragon_column_bg.png' },
      border:           { alias: 'dragon:border',     path: 'brand/border_forest.png' },
    },
  },
};
